/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';

import { ApiStatus } from '@ibm-watson/shared/ts';
import type { IntegrationSchema } from '@ibm-watson/shared/ts/integration';
import { integrationsAdapter } from '../adapter';
import {
  closeClassicIntegration,
  createIntegration,
  deleteIntegration,
  describeIntegration,
  listAllAssistantsIntegrations,
  listIntegrations,
  loadCurrentIntegration,
  resetDraftIntegrationForm,
  selectClassicIntegration,
  subscribeIntegration,
  updateIntegration
} from './actions';

export const extraReducers = builder => {
  builder
    // LIST INTEGRATIONS
    .addCase(listIntegrations.pending, (state, _) => {
      state.listIntegrations.status = ApiStatus.LOADING;
      state.listIntegrations.error = null;
    })
    .addCase(listIntegrations.fulfilled, (state, action) => {
      state.listIntegrations.status = ApiStatus.SUCCEEDED;
      state.listIntegrations.fetched = Date.now();
      state.listIntegrations.error = null;

      integrationsAdapter.setAll(state, action.payload);
    })
    .addCase(listIntegrations.rejected, (state, action) => {
      state.listIntegrations.status = ApiStatus.ERROR;
      state.listIntegrations.error = action.error;
    })
    .addCase(listAllAssistantsIntegrations.pending, (state, _) => {
      state.listIntegrations.status = ApiStatus.LOADING;
      state.listIntegrations.error = null;
    })
    .addCase(listAllAssistantsIntegrations.fulfilled, (state, action) => {
      state.listIntegrations.status = ApiStatus.SUCCEEDED;
      state.listIntegrations.error = null;

      integrationsAdapter.setAll(state, action.payload);
    })
    .addCase(listAllAssistantsIntegrations.rejected, (state, action) => {
      state.listIntegrations.status = ApiStatus.ERROR;
      state.listIntegrations.error = action.error;
    })
    // LOAD INTEGRATION
    .addCase(loadCurrentIntegration.pending, (state, _) => {
      state.loadIntegration.status = ApiStatus.LOADING;
      state.loadIntegration.error = null;
    })
    .addCase(loadCurrentIntegration.fulfilled, (state, action) => {
      state.loadIntegration.status = ApiStatus.SUCCEEDED;
      state.loadIntegration.error = null;

      state.currentIntegrationId = action.payload?.integration_id;
      state.draftIntegration.integration = action.payload;

      state.draftIntegration.name.valid = true;
      state.draftIntegration.name.dirty = false;
      state.draftIntegration.settings.valid = true;
      state.draftIntegration.settings.dirty = false;
    })
    .addCase(loadCurrentIntegration.rejected, (state, action) => {
      state.loadIntegration.status = ApiStatus.ERROR;
      state.loadIntegration.error = action.error;
    })
    // CREATE INTEGRATION
    .addCase(createIntegration.pending, (state, action) => {
      state.createIntegration.status = ApiStatus.LOADING;
      state.createIntegration.error = null;
      if (action.meta?.arg?.markAsPremature === true) {
        state.draftIntegration.integration_prematurely_created = true;
      } else {
        state.draftIntegration.integration_prematurely_created = null;
      }
    })
    .addCase(createIntegration.fulfilled, (state, action) => {
      state.createIntegration.status = ApiStatus.SUCCEEDED;
      state.createIntegration.error = null;

      state.draftIntegration.name.dirty = null;
      state.draftIntegration.settings.dirty = null;

      // make sure we're clearing away private settings after
      // saving so we don't keep track of sensitive values
      if (state.draftIntegration.integration?.settings?.private?.secrets) {
        _.set(
          state.draftIntegration.integration?.settings,
          'private.secrets',
          undefined
        );
      }

      // integrations in entity list should not contain private fields
      integrationsAdapter.addOne(
        state,
        _.omit(action.payload, [
          'settings.private.secrets'
        ]) as IntegrationSchema
      );

      // invalidate listIntegrations cache
      state.listIntegrations.fetched = null;
    })
    .addCase(createIntegration.rejected, (state, action) => {
      state.createIntegration.status = ApiStatus.ERROR;
      state.createIntegration.error = action.error;
    })
    // UPDATE INTEGRATION
    .addCase(updateIntegration.pending, (state, _) => {
      state.updateIntegration.status = ApiStatus.LOADING;
      state.updateIntegration.error = null;
    })
    .addCase(updateIntegration.fulfilled, (state, action) => {
      state.updateIntegration.status = ApiStatus.SUCCEEDED;
      state.updateIntegration.error = null;

      state.draftIntegration.integration_prematurely_created = null;

      // make sure we're clearing away private settings after
      // saving so we don't keep track of sensitive values
      if (state.draftIntegration.integration?.settings?.private?.secrets) {
        _.set(
          state.draftIntegration.integration?.settings,
          'private.secrets',
          undefined
        );
      }

      // integrations in entity list should not contain private fields
      integrationsAdapter.upsertOne(
        state,
        _.omit(action.payload, [
          'settings.private.secrets'
        ]) as IntegrationSchema
      );

      // invalidate listIntegrations cache
      state.listIntegrations.fetched = null;
    })
    .addCase(updateIntegration.rejected, (state, action) => {
      state.updateIntegration.status = ApiStatus.ERROR;
      state.updateIntegration.error = action.error;
    })
    // DELETE INTEGRATION
    .addCase(deleteIntegration.pending, (state, _) => {
      state.deleteIntegration.status = ApiStatus.LOADING;
      state.deleteIntegration.error = null;
    })
    .addCase(deleteIntegration.fulfilled, (state, action) => {
      const { integrationId, serviceDeskId } = action.payload;

      state.deleteIntegration.status = ApiStatus.SUCCEEDED;
      state.deleteIntegration.error = null;

      integrationsAdapter.removeOne(state, integrationId);
      if (!_.isNil(serviceDeskId)) {
        integrationsAdapter.removeOne(state, serviceDeskId);
      }

      // invalidate listIntegrations cache
      state.listIntegrations.fetched = null;
    })
    .addCase(deleteIntegration.rejected, (state, action) => {
      state.deleteIntegration.status = ApiStatus.ERROR;
      state.deleteIntegration.error = action.error;
    })
    // SUBSCRIBE INTEGRATION
    .addCase(subscribeIntegration.pending, (state, _action) => {
      state.subscribeIntegration.status = ApiStatus.LOADING;
      state.subscribeIntegration.error = null;
    })
    .addCase(subscribeIntegration.fulfilled, (state, action) => {
      state.subscribeIntegration.status = ApiStatus.SUCCEEDED;
      state.subscribeIntegration.error = null;

      // If a response data is returned, merge that with the draft
      // integration settings.
      if (
        !_.isEmpty(action.payload) &&
        !_.isEmpty(state.draftIntegration?.integration)
      ) {
        state.draftIntegration.integration.settings = _.merge(
          state.draftIntegration.integration?.settings,
          action.payload
        );

        integrationsAdapter.updateOne(state, {
          id: state.draftIntegration.integration.integration_id,
          changes: state.draftIntegration.integration
        });
      }
    })
    .addCase(subscribeIntegration.rejected, (state, action) => {
      state.subscribeIntegration.status = ApiStatus.ERROR;
      state.subscribeIntegration.error = action.error;
    })
    .addCase(describeIntegration.pending, (state, _action) => {
      state.describeIntegration.status = ApiStatus.LOADING;
      state.describeIntegration.error = null;
    })
    .addCase(describeIntegration.fulfilled, (state, action) => {
      state.describeIntegration.status = ApiStatus.SUCCEEDED;
      state.describeIntegration.error = null;

      state.legacy_service_desk = action.payload;
    })
    .addCase(describeIntegration.rejected, (state, action) => {
      state.describeIntegration.status = ApiStatus.ERROR;
      state.describeIntegration.error = action.error;
    })
    // OLD/CLASSIC IA
    .addCase(selectClassicIntegration, (state, action) => {
      state.currentIntegrationId = action.payload.integration_id;
      state.draftIntegration.show_overlay = action.payload.show_overlay;
      state.draftIntegration.integration = action.payload.integration;
      state.draftIntegration.channel_type = action.payload.channel_type;
    })
    .addCase(closeClassicIntegration, (state, _) => {
      state.currentIntegrationId = null;
      state.draftIntegration.show_overlay = false;
      state.draftIntegration.integration = null;
      state.draftIntegration.channel_type = null;
    })
    .addCase(resetDraftIntegrationForm, (state, action) => {
      state.draftIntegration.reset_integration_form = action.payload;
    });
};
